<template>
    <div>
        <div class="page-content px-4 top">
            <template-breadcrumb v-if="isDataLoaded" :title="details.title" />
            <template-header v-if="isDataLoaded" :title="details.title" />
            <b-card footer-class="p-0" class="rounded-lg">
                <div class="profile-details">
                    <form>
                        <input type="hidden" name="id" :value="templateId" />
                    </form>

                    <loader-profile
                        v-if="!isDataLoaded"
                        :show-image="false"
                    ></loader-profile>

                    <b-row v-else-if="isDataLoaded">
                        <b-col lg="6">
                            <b-form-group
                                label-for="title"
                                label-cols="3"
                                label="Name:"
                                class="title-alignment"
                            >
                                <b-input
                                    v-if="isEditMode"
                                    id="title"
                                    v-model="editDetails.title"
                                    :class="{
                                        'border-danger': errors.has('title')
                                    }"
                                    v-validate="'required'"
                                    name="title"
                                    type="text"
                                    placeholder="Title (required)"
                                    :disabled="isSavingEdit"
                                />
                                <span v-else>{{ details.title }}</span>
                            </b-form-group>
                            <b-form-group
                                label-for="bundle"
                                label-cols="3"
                                label="Type:"
                            >
                                <b-input plaintext value="Bundle (Estimate)" />
                            </b-form-group>
                            <b-form-group
                                label-for="description"
                                label-cols="3"
                                label="Description:"
                            >
                                <b-textarea
                                    v-if="isEditMode"
                                    v-model="editDetails.description"
                                    name="description"
                                    placeholder="Description (optional)"
                                    :disabled="isSavingEdit"
                                />
                                <div
                                    v-else
                                    class="mt-2"
                                    v-html="
                                        convertNewLineToBreak(
                                            details.description
                                        )
                                    "
                                ></div>
                            </b-form-group>
                        </b-col>
                        <b-col lg="6">
                            <b-form-group
                                label-for="lastUsed"
                                label-cols="3"
                                label="Last Used:"
                            >
                                <b-input
                                    plaintext
                                    :value="
                                        convertToDateFormat(details.last_used)
                                    "
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="updated"
                                label-cols="3"
                                label="Last Updated:"
                            >
                                <b-input
                                    plaintext
                                    :value="
                                        _carbonDateDisplay(details.update_at)
                                    "
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="totalUse"
                                label-cols="3"
                                label="Total Use:"
                            >
                                <b-input
                                    plaintext
                                    :value="details.total_used"
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="totalPrice"
                                label-cols="3"
                                label="Total Price:"
                            >
                                <b-input
                                    plaintext
                                    :value="details.total_price"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <template v-slot:footer no-body v-if="isDataLoaded">
                    <b-tabs
                        card
                        class="list tab-table-responsive"
                        lazy
                        v-model="activeTabIndex"
                        ref="btabs"
                    >
                        <tab-items
                            :key="1 + keyComponent"
                            ref="tabItems"
                            tabname="tabItems"
                            :tab-count="tabCount.item_count"
                            @reload-item-details="reloadPage"
                            @reload-tab-count="reloadTabCount"
                        />
                        <tab-activities
                            :key="2 + keyComponent"
                            ref="tabActivities"
                            tabname="tabActivities"
                            :tab-count="tabCount.activity_count"
                            @reload-tab-count="reloadTabCount"
                            @update-activity="updateActivity"
                        />
                        <tab-actions
                            :key="3 + keyComponent"
                            ref="tabActions"
                            tabname="tabActions"
                            :tab-count="tabCount.action_count"
                            @reload-tab-count="reloadTabCount"
                            @update-action="updateAction"
                        />
                    </b-tabs>
                </template>
            </b-card>
        </div>
        <modal-item ref="modalItem" @onsuccess="reloadPageDetails" />
        <ActionModal ref="actionModal" @refresh="reloadPage('tabActions')" />

        <ModalItemBundle
            ref="modalItemBundle"
            :template-id="details.id"
            v-if="details && details.id && !isChildrenBundle"
            @onsuccess="reloadPageDetails"
        />

        <ActivityModal
            ref="activityModal"
            :isTemplate="true"
            @refresh="reloadPage('tabActivities')"
        />
    </div>
</template>

<script>
import TemplateHeader from "@/module/settings/template/header.vue";
import TemplateBreadcrumb from "@/module/settings/template/breadcrumb.vue";
import TEMPLATES from "@/constants/templates";
import { DateTime } from "luxon";
import TEMPLATE_SERVICES from "@/services/templates";
import { assignIn, findIndex } from "lodash";
import TabItems from "@/module/settings/template/tabs/estimate/tab-items";
import TabActivities from "@/module/settings/template/tabs/invoice/tab-activities";
import TabActions from "@/module/settings/template/tabs/estimate/tab-actions";
import LoaderProfile from "@/components/elements/loader-profile.vue";
import ActionModal from "@/components/setting-template/bundle-estimate/modal/action";
import ModalItemBundle from "@/components/setting-template/bundle-estimate/modal-bundle";
import SettingTemplateMixin from "@/mixins/settings-template";

export default {
    mixins: [SettingTemplateMixin],
    components: {
        TemplateHeader,
        TemplateBreadcrumb,
        LoaderProfile,
        TabItems,
        TabActivities,
        TabActions,
        ActivityModal: () =>
            import("@/module/schedule/treatment-board/activity"),
        ModalItem: () =>
            import(
                "@/components/setting-template/bundle-estimate/modal-item.vue"
            ),
        ActionModal,
        ModalItemBundle
    },
    data() {
        return {
            keyComponent: 0,
            activeTabIndex: 0,
            templateId: null,
            isDataLoaded: false,
            isLoadingItems: true,
            popperOpt: {
                placement: "bottom-start",
                modifiers: {
                    flip: {
                        enabled: false
                    }
                },
                positionFixed: true
            },
            tabCount: {
                item_count: 0,
                activity_count: 0,
                action_count: 0
            }
        };
    },
    computed: {
        details: {
            get() {
                return this.$store.state.settingsTemplate.details;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setDetails", value);
            }
        },
        editDetails: {
            get() {
                return this.$store.state.settingsTemplate.editDetails;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setEditDetails", value);
            }
        },
        isEditMode: {
            get() {
                return this.$store.state.settingsTemplate.isEditMode;
            }
        },
        isSavingEdit() {
            return this.$store.state.settingsTemplate.isSavingEdit;
        },
        items: {
            get() {
                return this.$store.state.settingsTemplate.items;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setItems", value);
            }
        },
        routeParam() {
            return this.$route.params.id;
        },
        isChildrenBundle() {
            return this.$store.state.settingsTemplate.details
                .is_children_bundle;
        }
    },
    watch: {
        routeParam() {
            this.fetchData();
        }
    },
    mounted() {
        this.fetchData();

        this.$store.commit(
            "settingsTemplate/setTemplateType",
            TEMPLATES.TYPE.BUNDLE_ESTIMATE
        );
        this.$store.dispatch("references/getUOM");
    },
    beforeDestroy() {
        // clear vuex store
        this.$store.dispatch("settingsTemplate/reset");
    },
    methods: {
        async reloadPageDetails() {
            await this.fetchData();
        },
        errorClass(key) {
            return {
                "border-danger": this.errors.has(key)
            };
        },
        async fetchData() {
            try {
                this.isDataLoaded = false;
                let encodedTemplateId = this.$route.params.id;
                this.templateId = null;

                // fetch details
                let fetchDetails = await this.$http.get(
                    `${process.env.BARRAMUNDI}account/template/${encodedTemplateId}?encoded=true`
                );

                let res = fetchDetails.data.response;
                this.details = res.resources || res.resource;
                this.details.title = this.details.name;
                this.details.is_children_bundle = res.is_children_bundle;
                this.templateId = this.details.id;

                this.isDataLoaded = true;

                this.validateTemplateType(
                    this.details.type,
                    TEMPLATES.TYPE.BUNDLE_ESTIMATE
                );

                this.reloadTabCount();

                // store templateId in vuex
                this.$store.commit("settingsTemplate/setTemplateId", this.templateId); // prettier-ignore
                this.$store.commit(
                    "settingsTemplate/setModalItem",
                    this.$refs.modalItem
                );
            } catch (error) {
                console.log(error);
                this.isDataLoaded = true;
                this._message(error.message, "danger");
            }
        },
        convertToDateFormat(epochTimestamp) {
            if (!epochTimestamp) {
                return "";
            }

            // Convert epoch timestamp in seconds to milliseconds for Luxon
            const dateTime = DateTime.fromMillis(epochTimestamp * 1000);

            // Format the date as desired, e.g., "yyyy-MM-dd"
            return dateTime.toFormat(this._dateFormat);
        },
        async saveDetails() {
            let self = this;

            this.$store.commit("settingsTemplate/toggleSavingEdit", true);

            try {
                let isValid = await this.$validator.validateAll();

                if (!isValid) {
                    this.errors
                        .all()
                        .forEach(error => self._message(error, "danger"));
                    this.$store.commit(
                        "settingsTemplate/toggleSavingEdit",
                        false
                    );
                    return;
                }

                let res = await TEMPLATE_SERVICES.putUpdateGeneric(
                    this.templateId,
                    {
                        id: this.templateId,
                        type: this.editDetails.template_type.id,
                        name: this.editDetails.title,
                        description: this.editDetails.description
                    }
                );

                this.$store.commit("settingsTemplate/commitEditDetails");

                this.fetchData(res);

                this._message("Record saved");
            } catch (error) {
                console.log(error);
                this._message("Error saving data", "danger");
            }

            this.$store.commit("settingsTemplate/toggleSavingEdit", false);
        },
        reloadTabCount() {
            TEMPLATE_SERVICES.getTabCount(this.templateId).then(response => {
                if (response?.code == 200) {
                    assignIn(this.tabCount, response.resources);
                } else {
                    this._message(
                        "Error: Failed to fetch tabs' count",
                        "danger"
                    );
                }
            });
        },
        updateActivity(activity = false) {
            let params = {
                action: activity ? "edit" : "new",
                ...activity
            };
            this.$refs.activityModal.open(params);
        },
        updateAction(action = false) {
            let params = null;
            if (action) {
                params = {
                    ...action
                };
                params.action = action.action_type;
                params.action_type = action.action_type.id;
            }
            this.$refs.actionModal.open(params);
        },
        async reloadPage(tabName = "tabItems") {
            if (tabName == "tabItems") {
                await this.reloadPageDetails();
            }
            this.reloadTabList(tabName);
            this.reloadTabCount();
        },
        reloadTabList(tabName) {
            this.activeTabIndex = this.findTabIndex(tabName);
            this.keyComponent++;
        },
        findTabIndex(tabname) {
            return findIndex(this.$refs.btabs.$children, function (tab) {
                return tab.$attrs.tabname == tabname;
            });
        }
    }
};
</script>

<style scoped>
::v-deep .custom-flex .bv-no-focus-ring {
    display: flex;
    align-items: center;
}
.top {
    margin-top: 10px;
    margin-bottom: 3px;
}
</style>
<style>
.title-alignment > div {
    margin-top: 0.5rem;
}
</style>
